import request, { type Variables } from 'graphql-request';
import { type TypedDocumentNode } from '@graphql-typed-document-node/core';
import {
	type QueryClient,
	useMutation,
	type UseMutationOptions,
	type UseMutationResult,
	useQuery,
	useQueryClient,
	UseQueryOptions,
	type UseQueryResult,
} from '@tanstack/react-query';
import { AuthContext, useAuth } from '../auth/useAuth';
import config from '../config/config';
import { useSnackbar } from '../store/snackbar';
import { useIntl } from 'react-intl';
import { DefinitionNode, OperationDefinitionNode } from 'graphql/language';
import { useOrganizationDomainContext } from '../contexts/OrganizationContext';
import { useContext, useEffect } from 'react';
import { sendErrorToSentry } from '../errorBoundary/sendErrorToSentry';

type CustomUseMutationOptions<R, E, V> = Omit<UseMutationOptions<R, E, V>, 'mutationFn'> & {
	readonly invalidateQueries?: boolean;
};

const isOperationDefinition = (definition: DefinitionNode): definition is OperationDefinitionNode => {
	return definition.kind === 'OperationDefinition';
};

export const getDocumentQueryKey = <R, V>(document: TypedDocumentNode<R, V>): string => {
	const operationDefinitions = document.definitions.filter(isOperationDefinition);

	if (operationDefinitions.length !== 1) {
		throw new Error('Expected exactly one operation definition within GraphQL document.');
	}

	if (operationDefinitions[0].name == null) {
		throw new Error('Expected GraphQL operation name to be defined.');
	}

	return operationDefinitions[0].name.value;
};

const getErrorResponseStatus = (error: unknown): number | null => {
	return typeof error === 'object' &&
		error != null &&
		'response' in error &&
		typeof error.response === 'object' &&
		error.response != null &&
		'status' in error.response &&
		typeof error.response.status === 'number'
		? error.response.status
		: null;
};

const onError = (setSignedOut: () => void, error: unknown, queryClient: QueryClient): void => {
	if (getErrorResponseStatus(error) === 401) {
		setSignedOut();
		queryClient.clear();
		return;
	}
};

const usePublicApiQuery = <R, E, V extends Variables | undefined>(
	document: TypedDocumentNode<R, V>,
	options?: Omit<UseQueryOptions<R, E, R>, 'queryFn' | 'queryKey'> & { variables?: V },
): UseQueryResult<R, E> => {
	const auth = useAuth();
	const intl = useIntl();
	const { organizationSlug } = useOrganizationDomainContext();
	const { openSnackbar } = useSnackbar();

	// for sentry log
	const { name } = document.definitions[0] as OperationDefinitionNode;

	const query = useQuery<R, E, R>({
		queryKey: ['public', getDocumentQueryKey(document), options?.variables],
		queryFn: async () => {
			// NOTE: we allow this method while auth is loading.
			if (!auth.isLoading && auth.isSignedIn) {
				throw new Error('Cannot use usePublicApiQuery hook with a signed in user.');
			}

			return request({
				url: `${config.apigw.base}/${organizationSlug}`,
				document,
				variables: options?.variables ?? {},
				requestHeaders: {
					Authorization: `Bearer ${config.contember.loginToken}`,
				},
			});
		},
		...options,
	});

	useEffect(() => {
		if (query.error) {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.error.default' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
			sendErrorToSentry({ error: query.error, module: 'api_error_mutation', activity: name?.value });
		}
	}, [query.error, intl, openSnackbar, name?.value]);

	return query;
};

const usePublicApiMutation = <R, E, V extends Variables | undefined>(
	document: TypedDocumentNode<R, V>,
	options?: CustomUseMutationOptions<R, E, V>,
): UseMutationResult<R, E, V> => {
	const auth = useAuth();
	const intl = useIntl();
	const queryClient = useQueryClient();
	const { organizationSlug } = useOrganizationDomainContext();
	const { openSnackbar } = useSnackbar();

	// for sentry log
	const { name } = document.definitions[0] as OperationDefinitionNode;

	return useMutation({
		mutationFn: async (variables: V) => {
			// NOTE: we allow this method while auth is loading.
			if (!auth.isLoading && auth.isSignedIn) {
				throw new Error('Cannot use usePublicApiMutation hook with a signed in user.');
			}

			return request({
				url: `${config.apigw.base}/${organizationSlug}`,
				document,
				variables,
				requestHeaders: {
					Authorization: `Bearer ${config.contember.loginToken}`,
				},
			});
		},
		onError: (error) => {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.error.default' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
			sendErrorToSentry({ error, module: 'api_error_public_mutation', activity: name?.value });
		},
		...options,
		onSuccess: async (data, variables, context) => {
			if (options?.invalidateQueries !== false) {
				await queryClient.invalidateQueries();
			}
			await options?.onSuccess?.(data, variables, context);
		},
	});
};

const useApiQuery = <R, E, V extends Variables | undefined>(
	document: TypedDocumentNode<R, V>,
	options?: Omit<UseQueryOptions<R, E, R>, 'queryFn' | 'queryKey'> & { variables?: V; validateResult?: (result: R) => void },
): UseQueryResult<R, E> => {
	const intl = useIntl();
	const queryClient = useQueryClient();
	const { organizationSlug } = useOrganizationDomainContext();
	const { setSignedOut } = useContext(AuthContext);
	const auth = useAuth();
	const { openSnackbar } = useSnackbar();

	const query = useQuery<R, E, R>({
		queryKey: [getDocumentQueryKey(document), options?.variables],
		queryFn: async () => {
			if (auth.isLoading) {
				throw new Error('Cannot use useApiQuery hook before authentication has loaded.');
			}

			if (!auth.isSignedIn) {
				throw new Error('Cannot use useApiQuery hook without a signed in user.');
			}

			const result = await request({
				url: `${config.apigw.base}/${organizationSlug}`,
				document,
				variables: options?.variables ?? {},
				requestHeaders: {
					Authorization: `Bearer ${auth.token}`,
				},
			});

			options?.validateResult?.(result);

			return result;
		},
		...options,
	});

	// for sentry log
	const { name } = document.definitions[0] as OperationDefinitionNode;

	useEffect(() => {
		if (query.error) {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.error.default' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
			onError(setSignedOut, query.error, queryClient);
			sendErrorToSentry({ error: query.error, module: 'api_error_mutation', activity: name?.value });
		}
		// omitting setSignedOut from deps as it does not have stable reference
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query.error, intl]);

	return query;
};

const useApiMutation = <R, E, V extends Variables | undefined>(
	document: TypedDocumentNode<R, V>,
	options?: CustomUseMutationOptions<R, E, V>,
): UseMutationResult<R, E, V> => {
	const intl = useIntl();
	const queryClient = useQueryClient();
	const { organizationSlug } = useOrganizationDomainContext();
	const { setSignedOut } = useContext(AuthContext);
	const auth = useAuth();
	const { openSnackbar } = useSnackbar();

	// for sentry log
	const { name } = document.definitions[0] as OperationDefinitionNode;

	return useMutation({
		mutationFn: async (variables: V) => {
			if (auth.isLoading) {
				throw new Error('Cannot use useApiMutation hook before authentication has loaded.');
			}

			if (!auth.isSignedIn) {
				throw new Error('Cannot use useApiMutation hook without a signed in user.');
			}

			return request({
				url: `${config.apigw.base}/${organizationSlug}`,
				document,
				variables,
				requestHeaders: {
					Authorization: `Bearer ${auth.token}`,
				},
			});
		},
		onError: (error) => {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.error.default' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
			sendErrorToSentry({ error, module: 'api_error_mutation', activity: name?.value });
			onError(setSignedOut, error, queryClient);
		},
		...options,
		onSuccess: async (data, variables, context) => {
			if (options?.invalidateQueries !== false) {
				await queryClient.invalidateQueries();
			}
			await options?.onSuccess?.(data, variables, context);
		},
	});
};

export { useApiQuery, useApiMutation, usePublicApiQuery, usePublicApiMutation };
